import { memo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Trash2, FileText, ReceiptText, Truck, Palette, Package, CalendarDays, Link2, Zap, HandCoins, CheckCircle2, StickyNote, PackageSearch } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import ProductPicker from "@/components/ProductPicker";
import api from "@/lib/api";
import { TRY, NUM, fmtDate, unitLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { EditableCell, SelectCell } from "./EditableCell";
import { ROW_COLORS, STATUS, UNITS, colorBg, lookupByCode } from "./ledgerUtils";
import { veresiyeRemaining } from "@/lib/pos";
import { toast } from "sonner";

export function ColorPicker({ value, onPick, testid, compact = false, defaultOpen = false, onClosed }) {
  const [open, setOpenRaw] = useState(defaultOpen);
  const setOpen = (o) => { setOpenRaw(o); if (!o) onClosed?.(); };
  const cur = ROW_COLORS.find((c) => c.key === value) || ROW_COLORS[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title={`Satır rengi: ${cur.label}`} data-testid={testid}
          className={cn("inline-flex items-center justify-center rounded-full border border-slate-300 hover:scale-110 transition-transform", compact ? "h-5 w-5" : "h-7 w-7 gap-1")}
          style={{ background: cur.bg || "#fff" }}>
          {!compact && <Palette className="h-3.5 w-3.5 text-slate-500" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        <div className="grid grid-cols-4 gap-1.5">
          {ROW_COLORS.map((c) => (
            <button key={c.key || "none"} type="button" title={c.label} onClick={() => { onPick(c.key); setOpen(false); }}
              className={cn("h-7 w-7 rounded-full border-2 transition-transform hover:scale-110", value === c.key ? "border-primary" : "border-white shadow")}
              style={{ background: c.bg || "linear-gradient(135deg,#fff 45%,#cbd5e1 50%,#fff 55%)" }} data-testid={testid ? `${testid}-${c.key || "none"}` : undefined} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function StatusBadge({ status, onToggle, testid, small = false }) {
  const s = STATUS[status] || STATUS.teslim;
  return (
    <button type="button" onClick={onToggle} title="Durumu değiştir" data-testid={testid}
      className={cn("inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wide whitespace-nowrap transition-colors hover:brightness-95", s.cls, small ? "px-1.5 py-px text-[9px]" : "px-2 py-0.5 text-[10px]")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", status === "bekliyor" ? "bg-amber-500 animate-pulse" : "bg-emerald-500")} />{s.label}
    </button>
  );
}

// Ağır açılır bileşenler (takvim, renk, stok arama, menü) yalnız tıklanınca bağlanır → yüzlerce satırlı defterde açılış/kaydırma/yazma akıcı kalır
function useLazy() {
  const [on, setOn] = useState(false);
  return [on, () => setOn(true), () => setOn(false)];
}

function RowMenu({ r, tid, isAdmin, h, onAddDesc }) {
  const [on, mount, unmount] = useLazy();
  const trigger = (props) => <button type="button" className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-foreground border border-transparent hover:border-slate-300" data-testid={`${tid}-menu`} {...props}><MoreHorizontal className="h-4 w-4" /></button>;
  if (!on) return trigger({ onClick: mount });
  return (
    <DropdownMenu defaultOpen onOpenChange={(o) => { if (!o) unmount(); }}>
      <DropdownMenuTrigger asChild>{trigger({})}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => h.openEntry(r.entryId)} data-testid={`${tid}-edit`}><Pencil className="h-4 w-4 mr-2" />Kaydı Düzenle (tüm kalemler)</DropdownMenuItem>
        <DropdownMenuItem onClick={onAddDesc} data-testid={`${tid}-add-desc`}><StickyNote className="h-4 w-4 mr-2 text-amber-500" />{(r.description && String(r.description).trim()) ? "Açıklamayı Düzenle" : "Açıklama Ekle"}</DropdownMenuItem>
        {isAdmin && <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => h.convert("quote", [r.key])} data-testid={`${tid}-to-quote`}><FileText className="h-4 w-4 mr-2 text-blue-600" />Teklife Dönüştür</DropdownMenuItem>
          <DropdownMenuItem onClick={() => h.convert("invoice", [r.key])} data-testid={`${tid}-to-invoice`}><ReceiptText className="h-4 w-4 mr-2 text-emerald-600" />Fatura Kes</DropdownMenuItem>
          <DropdownMenuItem onClick={() => h.convert("prep", [r.key])} data-testid={`${tid}-to-prep`}><Truck className="h-4 w-4 mr-2 text-primary" />Depoya Hazırlığa Gönder</DropdownMenuItem>
        </>}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => h.del(r)} data-testid={`${tid}-delete`}><Trash2 className="h-4 w-4 mr-2" />Satırı Sil</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DateCell({ r, tid, h }) {
  const [on, mount, unmount] = useLazy();
  const cls = cn("h-8 text-xs border-transparent bg-transparent hover:border-slate-300 hover:bg-white/70 px-2 w-full justify-start", !r.date && "text-amber-600 italic");
  if (on) return <DatePicker defaultOpen onClosed={unmount} value={r.date} onChange={(v) => v && v !== r.date && h.patch(r, "date", v)} placeholder="— tarih yok —" className={cls} data-testid={`${tid}-date`} />;
  return (
    <button type="button" onClick={mount} className={cn("inline-flex items-center rounded-md border font-normal whitespace-nowrap overflow-hidden", cls)} data-testid={`${tid}-date`}>
      <CalendarDays className="mr-2 h-4 w-4 shrink-0" />{r.date ? fmtDate(r.date) : "— tarih yok —"}
    </button>
  );
}

function RowColor({ r, tid, h }) {
  const [on, mount, unmount] = useLazy();
  if (on) return <ColorPicker defaultOpen onClosed={unmount} value={r.color} onPick={(k) => h.patch(r, "color", k)} testid={`${tid}-color`} compact />;
  const cur = ROW_COLORS.find((c) => c.key === r.color) || ROW_COLORS[0];
  return <button type="button" onClick={mount} title={`Satır rengi: ${cur.label}`} data-testid={`${tid}-color`} className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 hover:scale-110 transition-transform" style={{ background: cur.bg || "#fff" }} />;
}

function LinkStock({ tid, onSelect }) {
  const [on, mount, unmount] = useLazy();
  if (on) return <ProductPicker defaultOpen onClosed={unmount} onSelect={onSelect} label="" testid={`${tid}-link-stock`} className="h-6 w-6 px-0" />;
  return <button type="button" onClick={mount} className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent" data-testid={`${tid}-link-stock-trigger`}><PackageSearch className="h-4 w-4" /></button>;
}

// Sabit yükseklikli (h-11) defter satırı — tüm satırlar aynı boyutta; hücreye tıkla → düzenle.
// qsSale: satır hızlı satıştan geldiyse ilgili fiş (veresiye durumu + Tahsil Et için).
// h: CariLedgerTable'dan gelen SABİT işleyiciler (satır yalnız kendi verisi değişince yeniden çizilir).
function LedgerRowImpl({ r, selected, isAdmin, qsSale, h }) {
  const bg = colorBg(r.color);
  const tid = `lrow-${r.entryId}-${r.idx}`;
  const qsVeresiye = !!qsSale && (qsSale.payment_method === "veresiye" || Number(qsSale.veresiye_amount) > 0);
  const qsRemaining = qsSale ? veresiyeRemaining(qsSale) : 0;
  const qsOpen = qsVeresiye && !qsSale.cancelled && !qsSale.veresiye_paid && qsRemaining > 0.005;
  const [descOpen, setDescOpen] = useState(false);
  const hasTags = r.quoteNo || r.qsNo || qsVeresiye;
  const showDesc = !!(r.description && String(r.description).trim()) || descOpen;
  // Stoktan eşleştirme: fiyat + iskonto CARİ TİPİNE göre gelir (manuel fiyat → iskonto %0; değilse liste + carinin iskontosu). Stok düşülmez.
  const linkFields = (p) => {
    const pr = h.stockPricing(p);
    return { fields: { code: p.code || r.code || "", name: p.name, product_id: p.id, unit: unitLabel(p.unit || r.unit, "ADET"), unit_price: pr.unit_price || r.list_price, discount_rate: Number(pr.discount_rate) || 0, link_only: true }, pr };
  };
  const linkToast = (p, pr) => toast.success(`${p.name} · stokla eşleştirildi${pr.manual ? ` · manuel fiyat ${TRY(pr.unit_price)}` : pr.discount_rate ? ` · cari iskontosu %${NUM(pr.discount_rate)}` : ""} (stok düşülmedi)`);
  const patch = (f, v) => h.patch(r, f, v);
  const codeCommit = async (v) => {
    const code = String(v || "").trim();
    if (!code) { patch("code", ""); return; }
    const p = await lookupByCode(api, code);
    if (p) {
      const { fields, pr } = linkFields(p);
      h.patchMany(r, { ...fields, code: p.code || code });
      linkToast(p, pr);
    } else patch("code", code);
  };
  const linkFromStock = (p) => {
    const { fields, pr } = linkFields(p);
    h.patchMany(r, fields);
    linkToast(p, pr);
  };
  return (
    <tr className={cn("group h-11 border-b border-slate-200 transition-colors", selected ? "bg-primary/5" : "hover:bg-slate-50/80")} style={bg && !selected ? { background: bg } : undefined} data-testid={tid}>
      <td className="px-2 text-center w-8"><Checkbox checked={selected} onCheckedChange={() => h.toggle(r.key)} data-testid={`${tid}-check`} /></td>
      <td className="px-1 text-center text-xs font-mono text-slate-500 tabular-nums w-9">{r.sn}</td>
      <td className="px-0.5 w-[108px]"><DateCell r={r} tid={tid} h={h} /></td>
      <td className="px-0.5 align-middle">
        <div className="flex items-start gap-1 min-w-0 py-1">
          {r.linkedNoStock
            ? <span className="shrink-0 mt-1 inline-flex items-center justify-center h-4 w-4 rounded bg-violet-50 border border-violet-200 text-violet-700" title="Stok ürünüyle eşleştirildi — stok düşülmedi (geçmiş kayıt)"><Link2 className="h-2.5 w-2.5" /></span>
            : r.productId && <span className="shrink-0 mt-1 inline-flex items-center justify-center h-4 w-4 rounded bg-sky-50 border border-sky-200 text-sky-700" title="Stoktan seçildi — stok düşüldü"><Package className="h-2.5 w-2.5" /></span>}
          <div className="min-w-0 flex-1">
            <EditableCell value={r.name} onCommit={(v) => patch("name", v)} testid={`${tid}-name`} wrap className="font-semibold" />
            {(hasTags || showDesc) && (
              <div className="flex items-center gap-1 flex-wrap mt-0.5 px-2">
                {r.quoteNo && <span className="shrink-0 text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1" data-testid="ledger-quote-tag" title="Tekliften geldi">{r.quoteNo}</span>}
                {r.qsNo && (
                  <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1" data-testid="ledger-qs-tag" title={`Hızlı Satış fişinden geldi (${r.qsNo})${r.qsPay ? ` · ${r.qsPay === "veresiye" ? "Veresiye" : "ödeme alındı"}` : ""}`}>
                    <Zap className="h-2.5 w-2.5" />Hızlı Satış · {r.qsNo}
                  </span>
                )}
                {qsVeresiye && (qsSale.veresiye_paid
                  ? <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1" data-testid="ledger-qs-paid" title="Veresiye tahsil edildi"><CheckCircle2 className="h-2.5 w-2.5" />Tahsil edildi</span>
                  : <span className="shrink-0 text-[9px] font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded px-1" data-testid="ledger-qs-veresiye" title={`Veresiye · kalan ${TRY(qsRemaining)}`}>Veresiye{Number(qsSale.collected_total) > 0 ? ` · kalan ${TRY(qsRemaining)}` : ""}</span>)}
                {qsOpen && r.idx === 0 && (
                  <button type="button" onClick={() => h.collect(qsSale)} className="shrink-0 inline-flex items-center gap-0.5 h-5 px-1.5 rounded-md text-[9px] font-bold bg-amber-600 text-white hover:bg-amber-700" data-testid={`${tid}-collect`} title="Bu fişin veresiye tahsilatını al"><HandCoins className="h-2.5 w-2.5" />Tahsil Et</button>
                )}
                {showDesc && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 w-full sm:w-auto sm:flex-1 sm:min-w-[160px]" title="Açıklama">
                    <StickyNote className="h-3 w-3 shrink-0 text-amber-500" />
                    <EditableCell value={r.description} onCommit={(v) => { patch("description", v); if (!String(v).trim()) setDescOpen(false); }} testid={`${tid}-desc`} autoEdit={descOpen} wrap placeholder="açıklama yazın…" className="text-slate-600 italic text-xs py-0 min-h-0" />
                  </span>
                )}
              </div>
            )}
          </div>
          <span className="ml-auto mt-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0" title="Stok ürünüyle eşleştir (stok düşülmez)"><LinkStock tid={tid} onSelect={linkFromStock} /></span>
        </div>
      </td>
      <td className="px-0.5 w-[120px]"><EditableCell value={r.code} onCommit={codeCommit} testid={`${tid}-code`} placeholder="kod…" className="font-mono text-xs text-slate-600" title="Ürün kodu yazın → stoktan ad, birim ve liste fiyatı otomatik gelir" /></td>
      <td className="px-0.5 w-[76px]"><EditableCell value={r.quantity} type="number" align="right" format={(v) => NUM(v)} onCommit={(v) => patch("quantity", v)} testid={`${tid}-qty`} className="font-semibold" /></td>
      <td className="px-0.5 w-[92px]"><SelectCell value={r.unit} options={UNITS.includes(r.unit) ? UNITS : [r.unit, ...UNITS]} onCommit={(v) => patch("unit", v)} testid={`${tid}-unit`} /></td>
      <td className="px-0.5 w-[108px]"><EditableCell value={r.list_price} type="number" align="right" format={(v) => TRY(v)} onCommit={(v) => patch("unit_price", v)} testid={`${tid}-price`} /></td>
      <td className="px-0.5 w-[64px]"><EditableCell value={r.disc} type="number" align="right" format={(v) => (Number(v) ? `%${NUM(v)}` : "—")} onCommit={(v) => patch("discount_rate", v)} testid={`${tid}-disc`} className={r.disc ? "text-orange-600 font-semibold" : ""} /></td>
      <td className="px-2 w-[104px] text-right text-sm tabular-nums text-slate-600 whitespace-nowrap">{TRY(r.net_price)}</td>
      <td className="px-2 w-[118px] text-right text-sm font-bold tabular-nums whitespace-nowrap" data-testid={`${tid}-total`}>{TRY(r.tutar)}</td>
      <td className="px-1 w-[116px]"><StatusBadge status={r.status} onToggle={() => patch("status", r.status === "bekliyor" ? "teslim" : "bekliyor")} testid={`${tid}-status`} /></td>
      <td className="px-1 w-[60px]">
        <div className="flex items-center justify-end gap-1">
          <RowColor r={r} tid={tid} h={h} />
          <RowMenu r={r} tid={tid} isAdmin={isAdmin} h={h} onAddDesc={() => setDescOpen(true)} />
        </div>
      </td>
    </tr>
  );
}

// Satır yalnız KENDİ alanları / seçimi / fiş durumu değişince yeniden çizilir (entry/it nesne kimliği karşılaştırılmaz)
const ROW_SKIP = new Set(["entry", "it"]);
const sameRow = (a, b) => {
  if (a === b) return true;
  for (const k in a) { if (!ROW_SKIP.has(k) && a[k] !== b[k]) return false; }
  return true;
};
const qsKey = (s) => (s ? JSON.stringify(s) : "");
export const LedgerRow = memo(LedgerRowImpl, (a, b) => a.selected === b.selected && a.isAdmin === b.isAdmin && a.h === b.h && sameRow(a.r, b.r) && (a.qsSale === b.qsSale || qsKey(a.qsSale) === qsKey(b.qsSale)));

export const CalendarIcon = CalendarDays;
export const fmtRowDate = fmtDate;
